import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Check, Footprints, Plus, Users, Trash2, Camera, PartyPopper, Share2, Flag } from "lucide-react";
import { SafetyReportDialog } from "@/components/SafetyReport";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Avatar, BackButton, Empty, PageHeader, Spinner } from "@/components/Common";
import { Thumb } from "@/components/ContentGrid";
import { TZ } from "@/pages/Oggi";

const usePaths = () => useQuery({ queryKey: ["paths"], queryFn: () => api.get("/paths", { params: { tz: TZ } }).then((r) => r.data) });

function Progress({ me, days }: { me: any; days: number }) {
  if (!me) return null;
  return (
    <div className="space-y-1.5" data-testid="path-progress">
      <div className="h-1.5 rounded-full bg-white/60 overflow-hidden"><div className="h-full bg-[hsl(var(--primary))]" style={{ width: `${Math.min(100, (me.steps / days) * 100)}%` }} /></div>
      <div className="text-xs text-ink/66">{me.finished ? "Percorso completato" : `${me.steps} di ${days} giorni${me.done_today ? " · tappa di oggi fatta" : ""}`}</div>
    </div>
  );
}

export function PathCard({ p }: { p: any }) {
  return (
    <Link to={`/percorsi/${p.id}`} className="surface p-4 flex flex-col gap-3 hover:bg-white/60 min-h-[150px]" data-testid="path-card">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="chip">{p.days} giorni</span>
        <span className="chip">{p.category_name}</span>
        {!p.official && <span className="chip chip-violet">Proposto da un utente</span>}
      </div>
      <div className="font-semibold text-lg leading-tight">{p.title}</div>
      <div className="mt-auto flex items-center justify-between gap-3 text-sm text-ink/70">
        <span className="inline-flex items-center gap-1"><Users size={14} />{p.participants}</span>
        {p.me ? <span className="text-[hsl(var(--primary))] font-semibold inline-flex items-center gap-1"><Check size={14} />{p.me.finished ? "Completato" : `Giorno ${p.me.steps}/${p.days}`}</span>
          : <span className="font-semibold inline-flex items-center gap-1">Unisciti<ArrowRight size={14} /></span>}
      </div>
    </Link>
  );
}

function CreatePath({ onDone }: { onDone: (id: string) => void }) {
  const [f, setF] = useState({ title: "", description: "", category: "", days: 7 });
  const [busy, setBusy] = useState(false);
  const { data: cats } = useQuery({ queryKey: ["cats"], queryFn: () => api.get("/categories").then((r) => r.data) });
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!f.category) return toast.error("Scegli un tema");
    setBusy(true);
    try { const { data } = await api.post("/paths", f, { params: { tz: TZ } }); toast.success("Percorso creato: ci sei già dentro"); onDone(data.id); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="surface p-4 space-y-3" data-testid="path-create-form">
      <input className="field" placeholder="Titolo, es. Leggiamo insieme" maxLength={60} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} aria-label="Titolo" data-testid="path-title-input" />
      <textarea className="field min-h-[80px]" placeholder="Cosa si fa ogni giorno? (facoltativo)" maxLength={300} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} aria-label="Descrizione" data-testid="path-description-input" />
      <select className="field" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} aria-label="Tema" data-testid="path-category-select">
        <option value="">Scegli un tema</option>
        {(cats || []).map((c: any) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
      </select>
      <div className="seg grid grid-cols-4" role="radiogroup" aria-label="Durata">
        {[3, 5, 7, 14].map((d) => <button type="button" key={d} role="radio" aria-checked={f.days === d} onClick={() => setF({ ...f, days: d })} className={`seg-item justify-center ${f.days === d ? "seg-active" : ""}`} data-testid={`path-days-${d}`}>{d} giorni</button>)}
      </div>
      <button className="btn btn-primary w-full" disabled={busy || f.title.trim().length < 3} data-testid="path-create-submit">Crea il percorso</button>
    </form>
  );
}

export default function Percorsi() {
  const { data, isLoading } = usePaths();
  const [creating, setCreating] = useState(false);
  const nav = useNavigate();
  return (
    <div data-testid="percorsi-page">
      <PageHeader kicker="Facciamolo insieme" title="Percorsi" back="/cresciamo" />
      <div className="px-5 md:px-10 max-w-3xl space-y-6 pb-12">
        <p className="text-ink/70 leading-relaxed -mt-2">Percorsi di più giorni da fare insieme ad altre persone. Ogni giorno pubblichi una tappa. Nessun vincitore: si arriva in fondo insieme.</p>
        {creating ? <CreatePath onDone={(id) => nav(`/percorsi/${id}`)} /> : <button className="btn btn-ghost" onClick={() => setCreating(true)} data-testid="path-create-open"><Plus size={16} />Proponi un percorso</button>}
        {isLoading ? <Spinner /> : !data?.length ? <Empty testId="paths-empty">Nessun percorso disponibile.</Empty> : (
          <div className="grid sm:grid-cols-2 gap-3" data-testid="paths-list">{data.map((p: any) => <PathCard key={p.id} p={p} />)}</div>
        )}
      </div>
    </div>
  );
}

function PathActions({ p }: { p: any }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const refresh = () => ["path", "paths"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const call = async (fn: () => Promise<any>, msg?: string) => { try { await fn(); refresh(); if (msg) toast.success(msg); } catch (e) { toast.error(errMsg(e)); } };
  const leave = () => window.confirm("Uscire dal percorso? I tuoi post restano nel feed, ma i giorni fatti si azzerano.") && call(() => api.delete(`/paths/${p.id}/join`, { params: { tz: TZ } }));
  const remove = async () => {
    if (!window.confirm("Eliminare questo percorso per tutti?")) return;
    try { await api.delete(`/paths/${p.id}`); refresh(); nav("/percorsi"); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!p.me) return <button className="btn btn-primary" onClick={() => call(() => api.post(`/paths/${p.id}/join`, null, { params: { tz: TZ } }), "Sei dentro!")} data-testid="path-join-btn">Unisciti al percorso</button>;
  return (
    <div className="flex flex-wrap gap-2">
      {!p.me.finished && (p.me.done_today
        ? <span className="chip chip-lime min-h-[40px]" data-testid="path-done-today"><Check size={14} />Tappa di oggi pubblicata: torna domani</span>
        : <Link to={`/pubblica?path=${p.id}`} className="btn btn-primary" data-testid="path-publish-btn"><Camera size={16} />Pubblica la tappa di oggi</Link>)}
      <button className="btn btn-ghost" onClick={leave} data-testid="path-leave-btn">Esci</button>
      {p.is_mine && <button className="btn btn-danger" onClick={remove} data-testid="path-delete-btn"><Trash2 size={15} />Elimina</button>}
    </div>
  );
}

function ReportPath({ p }: { p: any }) {
  const [open, setOpen] = useState(false);
  if (p.official || p.is_mine) return null;
  return (
    <div>
      <button className="text-sm text-ink/60 inline-flex items-center gap-1.5 min-h-[40px]" onClick={() => setOpen(true)} data-testid="path-report-btn"><Flag size={14} />Segnala questo percorso</button>
      <SafetyReportDialog url={`/paths/${p.id}/report`} title="Segnala percorso" open={open} onOpenChange={setOpen} />
    </div>
  );
}

function Recap({ p }: { p: any }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const share = async () => {
    setBusy(true);
    try { await api.post(`/paths/${p.id}/share`); toast.success("Riepilogo pubblicato nel feed"); qc.invalidateQueries({ queryKey: ["path"] }); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <section className="surface p-5 space-y-4 relative overflow-hidden" data-testid="path-recap">
      <div className="celebrate" aria-hidden="true">{Array.from({ length: 14 }, (_, i) => <span key={`dot-${i}`} style={{ "--i": i } as React.CSSProperties} />)}</div>
      <div className="relative space-y-1">
        <div className="inline-flex items-center gap-2 text-[hsl(var(--primary))] font-semibold"><PartyPopper size={18} />Percorso completato</div>
        <h2 className="text-2xl font-semibold tracking-tight">Ce l'hai fatta: {p.days} giorni, un passo alla volta.</h2>
        <p className="text-sm text-ink/66">Questo riepilogo lo vedi solo tu.</p>
      </div>
      <ol className="relative space-y-2" data-testid="path-recap-steps">
        {(p.my_steps || []).map((s: any) => (
          <li key={s.day_num} className="flex items-center gap-3" data-testid="path-recap-step">
            <div className="w-14 shrink-0 rounded-xl overflow-hidden">{s.content ? <Thumb c={s.content} testId="path-recap-thumb" /> : <div className="aspect-[4/5] bg-white/60" />}</div>
            <div className="min-w-0"><div className="text-xs text-ink/55">Giorno {s.day_num} · {new Date(`${s.day}T12:00:00`).toLocaleDateString("it-IT", { day: "numeric", month: "short" })}</div><div className="text-sm font-medium">{s.prompt}</div></div>
          </li>
        ))}
      </ol>
      {p.recap_content_id
        ? <Link to={`/c/${p.recap_content_id}`} className="btn btn-ghost relative" data-testid="path-recap-shared"><Check size={15} />Condiviso nel feed: guarda il post</Link>
        : <button className="btn btn-primary relative" onClick={share} disabled={busy} data-testid="path-recap-share-btn"><Share2 size={15} />Condividi il riepilogo nel feed</button>}
    </section>
  );
}

export function PathPage() {
  const { id = "" } = useParams();
  const { data: p, isLoading, error } = useQuery({ queryKey: ["path", id], queryFn: () => api.get(`/paths/${id}`, { params: { tz: TZ } }).then((r) => r.data) });
  if (isLoading) return <div className="p-10"><Spinner /></div>;
  if (error || !p) return <div className="p-10"><Empty testId="path-not-found">Percorso non trovato.</Empty></div>;
  return (
    <div className="px-5 md:px-10 pt-6 md:pt-10 pb-12 max-w-3xl space-y-7" data-testid="path-page">
      <BackButton parent="/percorsi" />
      <header className="space-y-3">
        <div className="flex gap-2 flex-wrap"><span className="chip">{p.days} giorni</span><span className="chip">{p.category_name}</span><span className="chip"><Users size={12} />{p.participants} {p.participants === 1 ? "partecipante" : "partecipanti"}</span></div>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight" data-testid="path-title">{p.title}</h1>
        {p.description && <p className="text-ink/70 leading-relaxed">{p.description}</p>}
      </header>
      {p.me?.finished && <Recap p={p} />}
      {p.me && !p.me.finished && <div className="surface p-4 space-y-3"><Progress me={p.me} days={p.days} />{p.me.next_prompt && !p.me.done_today && <p className="text-sm" data-testid="path-next-prompt"><span className="font-semibold">Giorno {p.me.next_day}:</span> {p.me.next_prompt}</p>}</div>}
      <PathActions p={p} />
      <ReportPath p={p} />
      <section className="space-y-2">
        <h2 className="font-semibold">Le tappe</h2>
        <ol className="surface p-2" data-testid="path-prompts">
          {p.prompts.map((t: string, i: number) => (
            <li key={`${i}-${t}`} className="row rounded-2xl">
              <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${p.me && i < p.me.steps ? "bg-[hsl(var(--primary))] text-white" : "bg-white/70"}`}>{p.me && i < p.me.steps ? <Check size={13} /> : i + 1}</span>
              <span className="text-sm">{t}</span>
            </li>
          ))}
        </ol>
      </section>
      <section className="space-y-2">
        <h2 className="font-semibold inline-flex items-center gap-2"><Footprints size={17} />Cosa stanno condividendo</h2>
        {!p.posts.length ? <Empty testId="path-posts-empty">Ancora nessuna tappa pubblicata. Puoi essere la prima persona.</Empty> : (
          <div className="grid grid-cols-3 gap-1 rounded-2xl overflow-hidden" data-testid="path-posts">
            {p.posts.map((c: any) => <div key={c.id} className="relative"><Thumb c={c} testId="path-post" /><span className="absolute bottom-1.5 left-1.5 chip bg-white/85 text-[10px]">G{c.path_day} · {c.creator?.display_name}</span></div>)}
          </div>
        )}
      </section>
      {p.people.length > 0 && (
        <section className="space-y-2">
          <h2 className="font-semibold">Chi fa il percorso</h2>
          <div className="flex flex-wrap gap-2" data-testid="path-people">{p.people.map((u: any) => <Link key={u.id} to={`/u/${u.username}`} className="chip min-h-[40px] pl-1" data-testid="path-person"><Avatar user={u} size={28} />{u.display_name}</Link>)}</div>
        </section>
      )}
    </div>
  );
}
